<template>
    <div class="disturbance-line-chart">
        <svg ref="lineAreaChart" />
        <div class="row">
            <div
                v-for="(legend, idx) in lineAreaLegend"
                :key="idx"
                class="row-item"
                @click="setLineAreaFilter(legend.filter)"
                @keypress="setLineAreaFilter(legend.filter)"
            >
                <div
                    v-if="legend.visible"
                    :class="legend.type==='solidLine'?'line-box':'color-box'"
                    :style="{ backgroundColor: legend.color}"
                />
                <div
                    v-else
                    class="color-box"
                />
                <div
                    :class="{'is-strikethrough': !legend.visible}"
                >
                    {{ legend.label }}
                </div>
            </div>
        </div>
        <div
            ref="chartPopup"
            class="chart-tooltip"
        >
            <h2>{{ popupContent.header }}</h2>
            <table>
                <tr
                    v-for="(d) in popupContent.data"
                    :key="d.key"
                    :class="[
                        'popup-row',
                        d.key.includes('Average') ? 'group' : ''
                    ]"
                >
                    <td class="key">
                        {{ d.key }}
                    </td>
                    <td class="value">
                        {{ d.value }}
                    </td>
                </tr>
            </table>
        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';

import scssVars from '@/assets/scss/_variables.scss';
import { autoFormatNumber } from '@/utilities/numUtils.js';

export default {
    name: 'ForestDisturbanceLineAreaChart',

    props: {
        chartData: {
            type: Array,
            default: () => [],
        },

        peakFlow: {
            type: Boolean,
            default: false,
        },

        chartClass: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            chartHeight: 230,
            chartWidthFallback: 900,
            xChartOffset: 45,
            colors: {
                mpb: scssVars.colorMountainPineBeetleDisturbance,
                harvest: scssVars.colorHarvestDisturbance,
                fire: scssVars.colorFireDisturbance,
            },

            marks: [
                {
                    type: 'line',
                    color: scssVars.colorInfrastructureDisturbance,
                    value: 'infra',
                    name: 'Infrastructure',
                },
                {
                    type: 'area',
                    color: scssVars.colorFireDisturbance,
                    min: 'fire_min',
                    max: 'fire_max',
                    minName: 'Fire Min',
                    maxName: 'Fire Max',
                },
                {
                    type: 'line',
                    color: '#77090b',
                    value: 'fire_avg',
                    name: 'Fire Average',
                },
                {
                    type: 'area',
                    color: scssVars.colorHarvestDisturbance,
                    min: 'harvest_min',
                    max: 'harvest_max',
                    minName: 'Harvest Min',
                    maxName: 'Harvest Max',
                },
                {
                    type: 'line',
                    color: '#3a8537',
                    value: 'harvest_avg',
                    name: 'Harvest Average',
                },
                {
                    type: 'area',
                    color: scssVars.colorMountainPineBeetleDisturbance,
                    min: 'mpb_min',
                    max: 'mpb_max',
                    minName: 'Mountain Pine Beetle Min',
                    maxName: 'Mountain Pine Beetle Max',
                },
                {
                    type: 'line',
                    color: '#572b5e',
                    value: 'mpb_avg',
                    name: 'Mountain Pine Beetle Average',
                },
            ],

            lineAreaLegend: [
                {
                    label: 'Mountain Pine Beetle',
                    filter: ['mpb_min', 'mpb_max', 'mpb_avg'],
                    color: scssVars.colorMountainPineBeetleDisturbance,
                    visible: true,
                },
                {
                    label: 'Harvest',
                    filter: ['harvest_min', 'harvest_max', 'harvest_avg'],
                    color: scssVars.colorHarvestDisturbance,
                    visible: true,
                },
                {
                    label: 'Fire',
                    filter: ['fire_min', 'fire_max', 'fire_avg'],
                    color: scssVars.colorFireDisturbance,
                    visible: true,
                },
                {
                    label: 'Infrastructure',
                    filter: ['infra'],
                    color: scssVars.colorInfrastructureDisturbance,
                    visible: true,
                    type: 'solidLine',
                },
            ],

            popupContent: {
                header: '',
                data: [],
            },
        };
    },

    computed: {
        // use window size for chart width on small screens
        // fall back to default width on larger devices
        chartWidth() {
            if (window.innerWidth <= 768) {
                return window.innerWidth;
            }
            return this.chartWidthFallback;
        },
    },

    mounted() {
        this.renderChart();
    },

    methods: {
        /**
         * Renders a Line Area Forest Disturbance Chart based on a set of data, using d3
         */
        renderChart() {
            // select a subset of the data
            // strictly speaking, we don't need to do this but it doesn't hurt when looking at the data
            let data = [];
            if (this.peakFlow) {
                data = this.chartData.map((r) => ({
                    year: r.year,
                    mpb_avg: r.mpb_avg_h60,
                    mpb_min: r.mpb_min_h60,
                    mpb_max: r.mpb_max_h60,
                    infra: r.infra_h60,
                    harvest_avg: r.harvest_avg_h60,
                    harvest_min: r.harvest_min_h60,
                    harvest_max: r.harvest_max_h60,
                    fire_avg: r.fire_avg_h60,
                    fire_min: r.fire_min_h60,
                    fire_max: r.fire_max_h60,
                }));
            } else {
                data = this.chartData.map((r) => ({
                    year: r.year,
                    mpb_avg: r.mpb_avg,
                    mpb_min: r.mpb_min,
                    mpb_max: r.mpb_max,
                    infra: r.infra,
                    harvest_avg: r.harvest_avg,
                    harvest_min: r.harvest_min,
                    harvest_max: r.harvest_max,
                    fire_avg: r.fire_avg,
                    fire_min: r.fire_min,
                    fire_max: r.fire_max,
                }));
            }

            // get a list of the years for the x axis and xScale.
            const years = data.map((row) => row.year);

            // start building out the svg chart, first by setting the viewbox
            const svg = d3.select(this.$refs.lineAreaChart);
            svg.attr('viewBox', `0 0 ${this.chartWidth + 60} ${this.chartHeight + 30}`);
            svg.selectAll('*').remove(); // clear it out
            const chart = svg.append('g')
                .attr('transform', `translate(${this.xChartOffset},10)`);

            // initialize y-scale
            const maxValue = d3.max(data.map((row) => {
                // only look at value sets that are visible
                const partial = [];
                this.lineAreaLegend.forEach((legend) => {
                    if (legend.visible) {
                        legend.filter.forEach((key) => partial.push(row[key]));
                    }
                });
                return d3.max(partial);
            }));

            const minValue = d3.min(data.map((row) => {
                // only look at value sets that are visible
                const partial = [];
                this.lineAreaLegend.forEach((legend) => {
                    if (legend.visible) {
                        legend.filter.forEach((key) => partial.push(row[key]));
                    }
                });
                return d3.min(partial);
            }));

            const yScale = d3.scaleLinear()
                .range([this.chartHeight, 0])
                .domain([minValue, maxValue]);

            // initialize x-scale
            const xScale = d3.scalePoint()
                .range([0, this.chartWidth])
                .domain(years);

            // initialize y-axis: ECA values along the left
            chart.append('g')
                .attr('class', 'axis')
                .call(d3.axisLeft(yScale));

            chart.append('text')
                .attr('transform', 'rotate(-90)')
                .attr('y', 0 - 35)
                .attr('x', 0 - (this.chartHeight / 2))
                .style('text-anchor', 'middle')
                .attr('class', 'axis-label')
                .text('ECA %');

            // grid lines
            chart.append('g')
                .call(
                    d3.axisRight(yScale)
                        .tickSize(this.chartWidth)
                        .tickFormat(() => ''),
                )
                .call((g) => g.selectAll('.domain').remove())
                .call((g) => g.selectAll('.tick line')
                    .attr('stroke-opacity', 0.15));

            // center diverging line for separating positives and negatives
            if (this.peakFlow) {
                const center = d3.scaleLinear()
                    .range([0, this.chartWidth]);
                const centerLine = d3.axisTop(center).ticks(0);
                chart.append('g')
                    .attr('class', 'centerline')
                    .attr('transform', `translate(0,${yScale(0)})`)
                    .call(centerLine);
            }

            let xAxisTickValues = years;

            // display less ticks on the x-axis on smaller devices
            if (this.chartWidth < this.chartWidthFallback) {
                xAxisTickValues = d3.range(d3.min(years), d3.max(years), 5);
            } else {
                xAxisTickValues = d3.range(d3.min(years), d3.max(years), 2);
            }

            // initialize x-axis: years along the bottom
            chart.append('g')
                .attr('class', 'axis')
                .attr('transform', `translate(0, ${this.chartHeight})`)
                .call(d3.axisBottom(xScale).tickValues(xAxisTickValues));

            // filter out the marks that are hidden by the legend object
            const filteredMarks = [];
            this.lineAreaLegend.forEach((legend) => {
                if (legend.visible) {
                    // get all the marks that match this legend filter
                    this.marks.forEach((mark) => {
                        if ((mark.min && legend.filter.includes(mark.min))
                            || (mark.value && legend.filter.includes(mark.value))) {
                            filteredMarks.push(mark);
                        }
                    });
                }
            });

            // select the chart popup for mouseovers
            const popup = d3.select(this.$refs.chartPopup);

            // make some closures here so we can provide them to the event listeners and not have to
            // either make an internal function nor duplicate the function.
            const mouseOver = (event, datum) => {
                // the x value in pixels of the pointer with respect to the canvas of this chart
                const [xRelative] = d3.pointer(event, this.$refs.lineAreaChart);
                // the width of a step
                const xStep = xScale.step();
                // calculate which step we're in minus transform/translate of fixed amount
                const xIdx = Math.round((xRelative - this.xChartOffset) / xStep);
                // get the year from the domain array
                const selectedYear = xScale.domain()[xIdx];

                const selectedData = datum.find((d) => d.year === selectedYear);
                if (selectedData) {
                    this.popupContent.data = [];
                    filteredMarks.forEach((mark) => {
                        if (mark.value) {
                            this.popupContent.data.push({
                                key: mark.name,
                                value: `${autoFormatNumber(selectedData[mark.value], '0.00')} %`,
                            });
                        } else {
                            this.popupContent.data.push({
                                key: mark.maxName,
                                value: `${autoFormatNumber(selectedData[mark.max], '0.00')} %`,
                            });
                            this.popupContent.data.push({
                                key: mark.minName,
                                value: `${autoFormatNumber(selectedData[mark.min], '0.00')} %`,
                            });
                        }
                    });
                    // show a total ECA if there are more than one
                    this.popupContent.header = selectedYear;
                    // if the event.clientX and popup width are outside the
                    // viewport width, then put it on the other side of the mouse
                    let xPos = event.clientX + 15;
                    if (xPos + popup.node().offsetWidth > window.innerWidth) {
                        xPos = event.clientX - 15 - popup.node().offsetWidth;
                    }
                    // fade in
                    popup.transition()
                        .duration(100)
                        .style('opacity', 1.0)
                        .style('visibility', 'visible');
                    popup.style('left', `${xPos}px`)
                        .style('top', `${(event.clientY - 28)}px`);
                }
            };

            // what to do when the mouse leaves an area
            const mouseOut = () => {
                popup.transition()
                    .duration(100)
                    .style('opacity', 0)
                    .style('visibility', 'hidden');
            };

            filteredMarks.forEach((mark) => {
                // make some area or line marks
                if (mark.type === 'line') {
                    chart.append('path')
                        .data([data])
                        .attr('d', d3.line()
                            .x((d) => xScale(d.year))
                            .y((d) => yScale(d[mark.value])))
                        .attr('class', 'line')
                        .attr('stroke', mark.color)
                        .on('mouseover', mouseOver)
                        .on('mousemove', mouseOver)
                        .on('mouseout', mouseOut);
                }
                if (mark.type === 'area') {
                    chart.append('path')
                        .data([data])
                        .attr('d', d3.area()
                            .x((d) => xScale(d.year))
                            .y0((d) => yScale(d[mark.min]))
                            .y1((d) => yScale(d[mark.max])))
                        .attr('class', 'area')
                        .attr('fill', mark.color)
                        .on('mouseover', mouseOver)
                        .on('mousemove', mouseOver)
                        .on('mouseout', mouseOut);
                }
            });

            /**
             * Vertical line
             */

            // vertical line hard coded between 2020-2021
            chart.append('line')
                .attr('x1', xScale(2021))
                .attr('y1', 0)
                .attr('x2', xScale(2021))
                .attr('y2', this.chartHeight)
                .attr('transform', 'translate(-1, 0)')
                .style('stroke-width', 2)
                .style('stroke', 'black');

            /**
             * Right label
             */

            // add background rect for label to the right of the vertical line
            // rect will be adjusted once text element details are known
            chart.append('rect')
                .attr('class', `${this.chartClass}-bg-right overlay-text-bg`)
                .attr('x', xScale(2021) + 5)
                .attr('rx', 2)
                .attr('ry', 2);

            // add text label to the right of the verical line
            chart.append('text')
                .attr('class', `${this.chartClass}-text-right overlay-text`)
                .attr('text-anchor', 'left')
                .attr('x', xScale(2021) + 5)
                .attr('y', 1)
                .text('Recovery of previous forest disturbances');

            // get the width/height of the right sided text label
            const rightLabel = d3.select(`.${this.chartClass}-text-right`).node().getBBox();

            // adjust label background to size correct based on text label size
            d3.select(`.${this.chartClass}-bg-right`)
                .attr('y', -rightLabel.height / 1.5)
                .attr('height', rightLabel.height)
                .attr('width', rightLabel.width + 5)
                .attr('transform', 'translate(-2, 0)');

            /**
             * Left label
             */

            // add background rect for label to the left of the vertical line
            // rect will be adjusted once text element details are known
            chart.append('rect')
                .attr('class', `${this.chartClass}-bg-left overlay-text-bg`)
                .attr('x', xScale(2021) - 5)
                .attr('rx', 2)
                .attr('ry', 2);

            // add text label to the left of the verical line
            chart.append('text')
                .attr('class', `${this.chartClass}-text-left overlay-text`)
                .attr('x', xScale(2021) - 5 - 270)
                .attr('y', 1)
                .text('Including recorded forest disturbances');

            // get the width/height of the left sided text label
            const leftLabel = d3.select(`.${this.chartClass}-text-left`).node().getBBox();

            // reposition the left text label to be the proper distance away from the line
            // based on the width of the text element
            d3.select(`.${this.chartClass}-text-left`)
                .attr('x', xScale(2021) - 7 - leftLabel.width);

            // adjust label background to size correct based on text label size
            d3.select(`.${this.chartClass}-bg-left`)
                .attr('x', xScale(2021) - 7 - leftLabel.width)
                .attr('y', -leftLabel.height / 1.5)
                .attr('height', leftLabel.height)
                .attr('width', leftLabel.width + 4)
                .attr('transform', 'translate(-2, 0)');
        },

        // sets the filters so values get shown or hidden on the chart
        setLineAreaFilter(param) {
            const idx = this.lineAreaLegend.findIndex((x) => x.filter === param);
            this.lineAreaLegend[idx].visible = !this.lineAreaLegend[idx].visible;
            this.renderChart();
        },
    },

};
</script>

<style lang="scss">
.disturbance-line-chart {
    .overlay-text {
        font-family: sans-serif;
        font-size: 0.8rem;
        fill: white;
    }

    .overlay-text-bg {
        fill: black;
        opacity: 0.5;
    }

    g.axis text,
    text.axis-label {
        font-family: 'Lato', sans-serif;
        font-size: 1rem;
        font-weight: 400;
    }

    path.line {
        stroke-width: 2px;
        fill: none;
    }

    path.area {
        opacity: 0.8;
    }

    .row {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-evenly;
        margin-bottom: 1em;

        .row-item {
            display: flex;
            align-items: center;
            cursor: pointer;

            .color-box {
                display: inline-block;
                height: 1em;
                width: 1em;
                border-radius: 0.2em;
                margin: 0 1em;
                background-color: #fff;
            }

            .line-box {
                display: inline-block;
                height: 4px;
                width: 1em;
                border-radius: 0.2em;
                margin: 0 1em;
                background-color: #fff;
            }

            .is-strikethrough {
                text-decoration: line-through;
            }
        }
    }

    div.chart-tooltip {
        padding: 8px;
        opacity: 0;
        position: fixed;
        visibility: hidden;
        z-index: 1000;
        font-family: sans-serif;
        font-size: 1rem;
        border-radius: 3px;
        box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
        background-color: rgba(255, 255, 255, 0.95);
        border: 1px solid #d9d9d9;

        h2 {
            font-weight: bold;
        }

        table {
            td.key {
                color: #808080;
                text-align: right;
                padding-right: 4px;
                white-space: nowrap;
            }

            td.value {
                text-align: right;
                color: #222;
            }

            .group:not(:last-of-type) {
                border-bottom: 1px solid #808080;
            }
        }
    }

    @include tablet {
        g.axis text,
        text.axis-label {
            font-size: 0.8em;
        }

        div.chart-tooltip {
            font-size: 12px;
        }
    }
}
</style>
